/* eslint-disable no-unused-vars */
import React, { useState, useMemo } from 'react';
import { PLATS, NM, COST_FIELDS, PRE_MONTHS, TOTAL_COST_MONTHS, toArrayIdx, toMonthIdx } from '../utils/constants';
import { calcPlatform, calcRoyalties } from '../utils/calc';
import { ml, mlSafe, fu, fp, fpd } from '../utils/format';
import { getPlatformState, getCostState, getDealTerms } from '../utils/stateFactory';

// Formatted number input — shows comma-separated on blur, raw on focus
function FormattedInput({ value, onChange, disabled, className = 'ci' }) {
  const [focused, setFocused] = React.useState(false);
  const display = focused
    ? (value || '')
    : (parseFloat(value) > 0 ? Math.round(parseFloat(value)).toLocaleString('en-US') : (value || ''));
  return (
    <input
      type={focused ? 'number' : 'text'}
      className={className}
      value={display}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value)}
    />
  );
}


function DLCSection({ platKey, st, set, setMonth, cols, readOnly, fc, convert, rc, startDate }) {
  const [dlcs, setDlcs] = React.useState(() => {
    try { return JSON.parse(st.dlcs || '[]'); } catch { return []; }
  });

  React.useEffect(() => {
    try { setDlcs(JSON.parse(st.dlcs || '[]')); } catch { setDlcs([]); }
  }, [st.dlcs]);

  const saveDlcs = (next) => {
    setDlcs(next);
    set('dlcs', JSON.stringify(next));
  };

  const addDLC = () => {
    saveDlcs([...dlcs, {
      id: Date.now().toString(),
      name: `DLC ${dlcs.length + 1}`,
      mode: 'attach',  // 'attach' | 'manual'
      netPrice: '',
      attachPct: Array(NM).fill(''),
      units: Array(NM).fill(''),
    }]);
  };

  const updateDLC = (id, field, value) => {
    saveDlcs(dlcs.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const updateDLCMonth = (id, field, idx, value) => {
    saveDlcs(dlcs.map(d => {
      if (d.id !== id) return d;
      const arr = [...(d[field] || Array(NM).fill(''))];
      arr[idx] = value;
      return { ...d, [field]: arr };
    }));
  };

  const removeDLC = (id) => saveDlcs(dlcs.filter(d => d.id !== id));

  // Platform fee rate (use 30% default, 25% for Steam over threshold)
  const feeRate = platKey === 'steam' ? 0.30 : 0.30;

  if (dlcs.length === 0) {
    return (
      <tr className="rc">
        <td colSpan={cols.length + 1} style={{ padding: '6px 8px' }}>
          {!readOnly && (
            <button onClick={addDLC}
              style={{ fontSize: 9, padding: '4px 12px', background: 'var(--blue-dim)',
                border: '1px solid var(--blue)', color: 'var(--blue)', borderRadius: 'var(--r)',
                cursor: 'pointer', letterSpacing: '.08em', textTransform: 'uppercase' }}>
              + Add DLC
            </button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <>
      {dlcs.map(dlc => {
        const dlcUnits = dlc.mode === 'attach'
          ? cols.map(i => Math.round(((parseFloat(dlc.attachPct[i]) || 0) / 100) * (parseFloat(st.units[i]) || 0)))
          : cols.map(i => parseFloat(dlc.units[i]) || 0);
        const netPrice = parseFloat(dlc.netPrice) || 0;
        const grossRaw = cols.map(i => dlcUnits[i] * netPrice);
        const netRaw   = grossRaw.map(g => g * (1 - feeRate));
        const netFC    = netRaw.map((v, ii) => (convert && fc !== 'USD') ? convert(v, fc, cols[ii]) : v);

        return (
          <React.Fragment key={dlc.id}>
            <tr className="rh">
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input className="lbl-i" value={dlc.name}
                    onChange={e => updateDLC(dlc.id, 'name', e.target.value)}
                    disabled={readOnly} style={{ fontWeight: 600 }} />
                  <div className="tgl-pair" style={{ marginLeft: 8 }}>
                    <button className={`tbtn yes ${dlc.mode === 'attach' ? 'on' : ''}`}
                      onClick={() => updateDLC(dlc.id, 'mode', 'attach')} disabled={readOnly}>Attach Rate</button>
                    <button className={`tbtn no ${dlc.mode === 'manual' ? 'on' : ''}`}
                      onClick={() => updateDLC(dlc.id, 'mode', 'manual')} disabled={readOnly}>Manual</button>
                  </div>
                  <span style={{ fontSize: 9, color: 'var(--t3)', marginLeft: 8 }}>ASP</span>
                  <input type="number" step="0.01" className="ni" style={{ width: 70 }}
                    value={dlc.netPrice} placeholder="0.00"
                    onChange={e => updateDLC(dlc.id, 'netPrice', e.target.value)} disabled={readOnly} />
                  {!readOnly && (
                    <button onClick={() => removeDLC(dlc.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t4)', fontSize: 14, marginLeft: 'auto' }}>×</button>
                  )}
                </div>
              </td>
              {cols.map(i => <td key={i} />)}
            </tr>
            {dlc.mode === 'attach' && (
              <tr className="ri">
                <td>Attach Rate %</td>
                {cols.map(i => (
                  <td key={i}>
                    <input type="number" className="cp" value={dlc.attachPct[i] || ''}
                      onChange={e => updateDLCMonth(dlc.id, 'attachPct', i, e.target.value)}
                      disabled={readOnly} />
                  </td>
                ))}
              </tr>
            )}
            {dlc.mode === 'manual' && (
              <tr className="ri">
                <td>Units</td>
                {cols.map(i => (
                  <td key={i}>
                    <FormattedInput value={dlc.units[i] || ''} disabled={readOnly}
                      onChange={val => updateDLCMonth(dlc.id, 'units', i, val)} />
                  </td>
                ))}
              </tr>
            )}
            <tr className="rc">
              <td>Units ({dlc.name})</td>
              {cols.map((ci, ii) => <td key={ci}>{dlcUnits[ii] > 0 ? Math.round(dlcUnits[ii]).toLocaleString() : '—'}</td>)}
            </tr>
            <tr className="rc">
              <td>ASP</td>
              {cols.map(ci => <td key={ci}>{netPrice ? '$' + netPrice.toFixed(2) : '—'}</td>)}
            </tr>
            <tr className="rc">
              <td>Gross Revenue ({dlc.name}) USD</td>
              {grossRaw.map((v, ii) => <td key={ii}>{v > 0 ? fu(v) : '—'}</td>)}
            </tr>
          </React.Fragment>
        );
      })}
      {/* Total Gross Revenue (Base + all DLCs) */}
      {(() => {
        const totalGrossRaw = cols.map((ci, ii) => {
          let t = rc.grossRaw[ci] || 0;
          dlcs.forEach(dlc => {
            const dlcUnits = dlc.mode === 'attach'
              ? Math.round(((parseFloat(dlc.attachPct[ci]) || 0) / 100) * (parseFloat(st.units[ci]) || 0))
              : parseFloat(dlc.units[ci]) || 0;
            t += dlcUnits * (parseFloat(dlc.netPrice) || 0);
          });
          return t;
        });
        const totalNetRaw  = totalGrossRaw.map(g => g * (1 - feeRate));
        const totalNetFC   = totalNetRaw.map((v, ii) => (convert && fc !== 'USD') ? convert(v, fc, cols[ii]) : v);
        return (
          <>
            <tr className="rt">
              <td>Total Gross Revenue (USD)</td>
              {totalGrossRaw.map((v, ii) => <td key={ii}>{v > 0 ? fu(v) : '—'}</td>)}
            </tr>
            <tr className="rt">
              <td>Total Net Revenue — Base Game (USD)</td>
              {totalNetRaw.map((v, ii) => <td key={ii}>{v > 0 ? fu(v) : '—'}</td>)}
            </tr>
            {fc !== 'USD' && (
              <tr className="rc">
                <td>Total Net Revenue — Base Game ({fc})</td>
                {totalNetFC.map((v, ii) => <td key={ii}>{v > 0 ? fu(v) : '—'}</td>)}
              </tr>
            )}
          </>
        );
      })()}
      <tr className="rc">
        <td colSpan={cols.length + 1} style={{ padding: '6px 8px' }}>
          {!readOnly && (
            <button onClick={addDLC}
              style={{ fontSize: 9, padding: '4px 12px', background: 'var(--blue-dim)',
                border: '1px solid var(--blue)', color: 'var(--blue)', borderRadius: 'var(--r)',
                cursor: 'pointer', letterSpacing: '.08em', textTransform: 'uppercase' }}>
              + Add DLC
            </button>
          )}
        </td>
      </tr>
    </>
  );
}

function PlatformSection({ game, platKey, platLabel, badge, PS, updatePS, readOnly = false, startDate = null, functionalCurrency, convert }) {
  const st = getPlatformState(PS, game, platKey);
  const rc = useMemo(() => calcPlatform(platKey, st), [platKey, st]); // eslint-disable-line react-hooks/exhaustive-deps
  const isOther = platKey === 'other';
  const fc = functionalCurrency || 'USD';

  const set = (field, value) => updatePS(game, platKey, s => { s[field] = value; });
  const setMonth = (field, idx, value) => updatePS(game, platKey, s => {
    s[field] = [...s[field]];
    s[field][idx] = value;
  });

  // Earliest launch index for this platform
  const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
  const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
  const firstLaunch = ea !== null && fl !== null ? Math.min(ea, fl)
    : ea !== null ? ea
    : fl !== null ? fl
    : null;

  // Columns: if firstLaunch is negative, data still starts at index 0 but label from launch date
  // If firstLaunch >= 0, start from that index in the data array
  const dataStartCol = firstLaunch !== null ? Math.max(0, firstLaunch) : 0;
  const cols = Array.from({ length: NM - dataStartCol }, (_, i) => dataStartCol + i);
  const colCount = cols.length;
  // Helper to get the display label for a data column index
  const colLabel = (dataIdx) => {
    if (firstLaunch === null || firstLaunch >= 0) return ml(dataIdx, startDate);
    // Pre-forecast launch: offset the display by firstLaunch amount
    return mlSafe(dataIdx + firstLaunch, startDate);
  };

  function dO() {
    return (
      <>
        <option value="">None</option>
        {Array.from({ length: NM + 24 }, (_, idx) => {
          const i = idx - 24; // allow up to 24 months before forecast
          let label;
          if (startDate) {
            const total = startDate.month + i;
            const y = startDate.year + Math.floor(total / 12);
            const mIdx = ((total % 12) + 12) % 12;
            const MNS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            label = MNS[mIdx] + ' ' + y;
          } else {
            label = i < 0 ? `${Math.abs(i)} months before start` : `Month ${i + 1}`;
          }
          return <option key={i} value={String(i)}>{label}</option>;
        })}
      </>
    );
  }

  const hasUnits = st.units.some(v => parseFloat(v) > 0);
  const missingRRP = !st.rrpEA && !st.rrpFL;

  // Net revenue in FC
  const netFC = rc.netRaw.map((v, i) => (v !== null && convert && fc !== 'USD') ? convert(v, fc, i) : v);

  return (
    <div className="plat">
      <div className="plat-head">
        <span className="plat-name" style={{ marginRight: "auto" }}>{platLabel}</span>
        {badge && <span className="badge">{badge}</span>}
        {firstLaunch !== null && !isOther && (
          <span className="badge" style={{ marginLeft: 'auto' }}>
            From {mlSafe(firstLaunch, startDate)}
          </span>
        )}
      </div>
      <div className="plat-body">
        {!isOther && (
          <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="ig-lbl" style={{ marginBottom: 6 }}>Early Access</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                <span className="ir-lbl">RRP</span>
                <input type="number" step="0.01" className="ni" value={st.rrpEA}
                  placeholder="0.00" onChange={e => set('rrpEA', e.target.value)} disabled={readOnly} />
                <span className="ir-lbl" style={{ marginLeft: 4 }}>Launch</span>
                <select value={st.launchEA} onChange={e => set('launchEA', e.target.value)} disabled={readOnly}>
                  {dO()}
                </select>
              </div>
            </div>
            <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
              <div className="ig-lbl" style={{ marginBottom: 6 }}>Pricing Mode</div>
              <select value={st.pricingMode || 'rrp'} onChange={e => set('pricingMode', e.target.value)} disabled={readOnly}
                style={{ fontSize: 11, padding: '4px 20px 4px 8px' }}>
                <option value="rrp">RRP</option>
                <option value="net">Net Price</option>
              </select>
            </div>
            <div>
              <div className="ig-lbl" style={{ marginBottom: 6 }}>Full Launch</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                <span className="ir-lbl">RRP</span>
                <input type="number" step="0.01" className="ni" value={st.rrpFL}
                  placeholder="0.00" onChange={e => set('rrpFL', e.target.value)} disabled={readOnly} />
                <span className="ir-lbl" style={{ marginLeft: 4 }}>Launch</span>
                <select value={st.launchFL} onChange={e => {
                  const v = e.target.value;
                  if (v !== '' && st.launchEA !== '' && parseInt(v) < parseInt(st.launchEA)) {
                    alert('Full Launch cannot be before Early Access.');
                    return;
                  }
                  set('launchFL', v);
                }} disabled={readOnly}>
                  {dO()}
                </select>
              </div>
            </div>
          </div>
        )}


        {isOther ? (
          <OtherPlatformSection st={st} set={set} setMonth={setMonth} readOnly={readOnly}
            fc={fc} convert={convert} startDate={startDate} />
        ) : (
          // Standard platforms
          <>
            {firstLaunch === null ? (
              <p style={{ fontSize: 11, color: 'var(--t3)', padding: '8px 0' }}>
                Set a launch date above to enter revenue data.
              </p>
            ) : (
              <div className="tw">
                <table className="dt">
                  <thead>
                    <tr>
                      <th></th>
                      {cols.map(i => <th key={i}>{colLabel(i)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="ri">
                      <td>Base Game Units</td>
                      {cols.map(i => (
                        <td key={i}>
                          <FormattedInput value={st.units[i]} disabled={readOnly}
                            onChange={val => setMonth('units', i, val)} />
                        </td>
                      ))}
                    </tr>
                    {hasUnits && missingRRP && (
                      <tr className="rc">
                        <td colSpan={colCount + 1} style={{ color: 'var(--red)', fontSize: 9 }}>
                          ⚠ Units entered but no RRP set — revenue will calculate as zero
                        </td>
                      </tr>
                    )}
                    <tr className="rc">
                      <td>RRP</td>
                      {cols.map(i => <td key={i}>{rc.rrpDisplay[i]}</td>)}
                    </tr>
                    {st.pricingMode !== 'net' && (<>
                    <tr className="ri">
                      <td>Regional Pricing %</td>
                      {cols.map(i => (
                        <td key={i}>
                          <input type="number" className="cp" value={st.regionalPct[i]}
                            onChange={e => setMonth('regionalPct', i, e.target.value)} disabled={readOnly} />
                        </td>
                      ))}
                    </tr>
                    <tr className="ri">
                      <td>Discount %</td>
                      {cols.map(i => (
                        <td key={i}>
                          <input type="number" className="cp" value={st.discountPct[i]}
                            onChange={e => setMonth('discountPct', i, e.target.value)} disabled={readOnly} />
                        </td>
                      ))}
                    </tr>
                    <tr className="ri">
                      <td>Days on Discount</td>
                      {cols.map(i => (
                        <td key={i}>
                          <input type="number" min="0" max="31" className="ci"
                            value={st.daysDiscount?.[i] || ''}
                            onChange={e => setMonth('daysDiscount', i, e.target.value)}
                            disabled={readOnly} placeholder="0" />
                        </td>
                      ))}
                    </tr>
                    </>)}
                    <tr className="rc">
                      <td>Gross Revenue — Base Game (USD)</td>
                      {cols.map(i => <td key={i}>{rc.gross[i]}</td>)}
                    </tr>
                    <tr className="rc">
                      <td>Platform Fee %</td>
                      {cols.map(i => <td key={i}>{rc.feePct[i] !== '—' ? Math.round(parseFloat(rc.feePct[i])) + '%' : '—'}</td>)}
                    </tr>
                    <tr className="rt">
                      <td>Net Revenue — Base Game (USD)</td>
                      {cols.map(i => <td key={i}>{rc.net[i]}</td>)}
                    </tr>
                    {fc !== 'USD' && (
                      <tr className="rc">
                        <td>Net Revenue — Base Game ({fc})</td>
                        {cols.map(i => (
                          <td key={i}>{netFC[i] !== null ? fu(netFC[i]) : '—'}</td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TotalsSection({ game, PS, startDate, functionalCurrency, convert }) {
  const enabledPlats = PLATS.filter(p => PS[game]?.[p.key]?.enabled);
  if (!enabledPlats.length) return null;
  const fc = functionalCurrency || 'USD';

  // Find earliest launch across all platforms
  let firstCol = null;
  enabledPlats.forEach(p => {
    const st = PS[game][p.key];
    const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
    const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
    const earliest = ea !== null && fl !== null ? Math.min(ea, fl) : ea ?? fl;
    if (earliest !== null && (firstCol === null || earliest < firstCol)) firstCol = earliest;
  });

  if (firstCol === null) return null;
  // Data arrays are 0-indexed; negative firstCol means pre-forecast launch
  const dataStart = Math.max(0, firstCol);
  const cols = Array.from({ length: NM - dataStart }, (_, i) => dataStart + i);

  const tg = Array(NM).fill(0), tn = Array(NM).fill(0);
  enabledPlats.forEach(p => {
    const rc = calcPlatform(p.key, PS[game][p.key]);
    rc.grossRaw.forEach((v, i) => { if (v !== null) tg[i] += v; });
    rc.netRaw.forEach((v, i)   => { if (v !== null) tn[i] += v; });
  });

  const anyGross = cols.some(i => tg[i] > 0);
  if (!anyGross) return null;

  const tnFC = tn.map((v, i) => convert && fc !== 'USD' ? convert(v, fc, i) : v);

  return (
    <div className="sb">
      <div className="sb-head">
        <span className="sb-title">Revenue Summary — All Platforms</span>
        <span className="card-meta">From {mlSafe(firstCol, startDate)}</span>
      </div>
      <div className="sb-body">
        <table className="dt">
          <thead>
            <tr><th></th>{cols.map(i => <th key={i}>{mlSafe(i, startDate)}</th>)}</tr>
          </thead>
          <tbody>
            <tr className="rc">
              <td>Total Gross Revenue USD</td>
              {cols.map(i => <td key={i}>{tg[i] > 0 ? fu(tg[i]) : '—'}</td>)}
            </tr>
            <tr className="rg">
              <td>Total Net Revenue USD</td>
              {cols.map(i => <td key={i}>{tn[i] > 0 ? fu(tn[i]) : '—'}</td>)}
            </tr>
            {fc !== 'USD' && (
              <tr className="rg">
                <td>Total Net Revenue {fc}</td>
                {cols.map(i => <td key={i}>{tnFC[i] > 0 ? fu(tnFC[i]) : '—'}</td>)}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function MilestoneAmountInput({ value, onChange, disabled }) {
  const [focused, setFocused] = React.useState(false);
  const num = parseFloat(value);
  const display = focused ? (value || '') : (num > 0 ? Math.round(num).toLocaleString('en-US') : (value || ''));
  return (
    <input
      type={focused ? 'number' : 'text'}
      value={display}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value)}
      className="ci"
      style={{ width: '100%' }}
    />
  );
}

const MAX_MILESTONES = 24;

const OTHER_DEFAULT_ROWS = [
  { key: 'platformDeal', label: 'Platform Deal',  fixed: true },
  { key: 'resellers',    label: 'Resellers',       fixed: true },
  { key: 'physical',     label: 'Physical',         fixed: true },
  { key: 'otherDeal',    label: 'Other',            fixed: true },
];

function OtherPlatformSection({ st, set, setMonth, readOnly, fc, convert, startDate }) {
  const [extraRows, setExtraRows] = React.useState(() => {
    // Parse any extra rows stored in customRow labels beyond the fixed ones
    return [];
  });

  const COLS = Array.from({ length: NM }, (_, i) => i);
  const hdr = <tr><th style={{textAlign:'left',minWidth:140}}></th>{COLS.map(i=><th key={i}>{mlSafe(i,startDate)}</th>)}</tr>;

  // Compute row total per month
  const rowKeys = ['platformDeal','resellers','physical','otherDeal'];

  const monthTotal = Array(NM).fill(0).map((_, i) => {
    return rowKeys.reduce((s, k) => s + (parseFloat(st[k]?.[i]) || 0), 0)
           + extraRows.reduce((s, r) => s + (parseFloat(st[r.key]?.[i]) || 0), 0);
  });

  return (
    <div className="tw">
      <table className="dt">
        <thead>{hdr}</thead>
        <tbody>
          {rowKeys.map(k => (
            <tr key={k} className="ri">
              <td>{OTHER_DEFAULT_ROWS.find(r=>r.key===k)?.label || k}</td>
              {COLS.map(i => (
                <td key={i}>
                  <FormattedInput value={st[k]?.[i] || ''} disabled={readOnly}
                    onChange={val => setMonth(k, i, val)} />
                </td>
              ))}
            </tr>
          ))}
          {extraRows.map(row => (
            <tr key={row.key} className="ri">
              <td>
                <input className="lbl-i" value={row.label} disabled={readOnly}
                  onChange={e => setExtraRows(prev => prev.map(r => r.key === row.key ? {...r, label: e.target.value} : r))} />
              </td>
              {COLS.map(i => (
                <td key={i}>
                  <FormattedInput value={st[row.key]?.[i] || ''} disabled={readOnly}
                    onChange={val => setMonth(row.key, i, val)} />
                </td>
              ))}
            </tr>
          ))}
          <tr className="rc">
            <td colSpan={NM+1}>
              {!readOnly && (
                <button style={{fontSize:9,padding:'3px 10px',background:'transparent',border:'1px dashed var(--e1)',color:'var(--t3)',borderRadius:'var(--r)',cursor:'pointer'}}
                  onClick={() => setExtraRows(prev => [...prev, { key: 'extra_'+Date.now(), label: 'New Row' }])}>
                  + Add Row
                </button>
              )}
            </td>
          </tr>
          <tr className="rt">
            <td>Net Revenue — Base Game (USD)</td>
            {COLS.map(i => <td key={i}>{monthTotal[i] ? fu(monthTotal[i]) : '—'}</td>)}
          </tr>
          {fc !== 'USD' && (
            <tr className="rc">
              <td>Net Revenue — Base Game ({fc})</td>
              {COLS.map(i => {
                const v = convert ? convert(monthTotal[i], fc, i) : monthTotal[i];
                return <td key={i}>{v ? fu(v) : '—'}</td>;
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}



function MilestonesSection({ game, milestones, onUpdateMilestones, readOnly, startDate, functionalCurrency, DT }) {
  const dt = getDealTerms(DT, game);
  const fc = functionalCurrency || 'USD';
  const ccy = dt.devAdvanceCurrency || fc;
  const MN2 = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Local state to prevent rerender issues during editing
  const [local, setLocal] = React.useState(() => {
    const arr = milestones[game] || [];
    const count = Math.min(MAX_MILESTONES, arr.filter(m => m.amount || m.month !== '').length + 1);
    return Array.from({ length: count }, (_, i) => arr[i] || { amount: '', month: '' });
  });

  const editingRef = React.useRef(false);

  // Sync from prop/Supabase when game changes or milestones load (but not during active edits)
  React.useEffect(() => {
    if (editingRef.current) return;
    const arr = milestones[game] || [];
    const count = Math.min(MAX_MILESTONES, arr.filter(m => m.amount || m.month !== '').length + 1);
    setLocal(Array.from({ length: Math.max(count, 1) }, (_, i) => arr[i] || { amount: '', month: '' }));
  }, [game, milestones]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMs = (idx, field, value) => {
    editingRef.current = true;
    setLocal(prev => {
      const next = prev.map((m, i) => i === idx ? { ...m, [field]: value } : m);
      const filled = next.filter(m => m.amount || m.month !== '').length;
      const withBlank = filled < MAX_MILESTONES && filled === next.length
        ? [...next, { amount: '', month: '' }] : next;
      onUpdateMilestones?.(game, withBlank);
      // Allow sync again after a short delay
      setTimeout(() => { editingRef.current = false; }, 1000);
      return withBlank;
    });
  };

  const displayed = local.length === 0 ? [{ amount: '', month: '' }] : local;

  return (
    <div className="sb" style={{ marginBottom: 14 }}>
      <div className="sb-head">
        <span className="sb-title">Development Advances ({ccy})</span>
        <span className="card-meta">Milestones auto-populate Recoupable Dev Advances below</span>
      </div>
      <div className="sb-body">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th style={{ textAlign: 'left', minWidth: 120 }}>Row</th>
                {displayed.map((_, i) => (
                  <th key={i} style={{ minWidth: 70, maxWidth: 80, width: 75 }}>MS{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="ri">
                <td>Amount ({ccy})</td>
                {displayed.map((ms, i) => (
                  <td key={i} style={{ minWidth: 70, maxWidth: 80, width: 75 }}>
                    <MilestoneAmountInput
                      value={ms.amount}
                      disabled={readOnly}
                      onChange={val => updateMs(i, 'amount', val)}
                    />
                  </td>
                ))}
              </tr>
              <tr className="ri">
                <td>Month</td>
                {displayed.map((ms, i) => (
                  <td key={i} style={{ minWidth: 70, maxWidth: 80, width: 75 }}>
                    <select
                      value={ms.month !== undefined && ms.month !== '' ? ms.month : ''}
                      disabled={readOnly}
                      onChange={e => updateMs(i, 'month', e.target.value !== '' ? parseInt(e.target.value) : '')}
                      style={{ fontSize: 10, padding: '2px 16px 2px 4px', width: '100%' }}>
                      <option value="">— month —</option>
                      {Array.from({ length: 60 }, (_, idx) => {
                        const monthIdx = idx - 24;
                        let label;
                        if (startDate) {
                          const total = startDate.month + monthIdx;
                          const y = startDate.year + Math.floor(total / 12);
                          const mIdx = ((total % 12) + 12) % 12;
                          label = MN2[mIdx] + ' ' + y;
                        } else {
                          label = monthIdx < 0 ? `${Math.abs(monthIdx)}m pre` : `Month ${monthIdx + 1}`;
                        }
                        return <option key={monthIdx} value={monthIdx}>{label}</option>;
                      })}
                    </select>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CostBlock({ costs, sec, game, updateCS, readOnly, startDate = null, devStartCol = 0, functionalCurrency }) {
  const fc = functionalCurrency || 'USD';
  const baseLabel = sec === 'recoup' ? 'Recoupable Costs' : 'Non-Recoupable Costs';
  // devStartCol is a month index (can be negative for pre-forecast)
  // Clamp to -PRE_MONTHS minimum
  const startMonthIdx = Math.max(-PRE_MONTHS, devStartCol ?? 0);
  // Generate month indices from startMonthIdx to NM-1
  const monthCols = Array.from({ length: NM - startMonthIdx }, (_, i) => startMonthIdx + i);

  // Calculate totals using array indices
  const tots = {};
  monthCols.forEach(mi => { tots[mi] = 0; });
  COST_FIELDS.forEach(({ key }) => {
    monthCols.forEach(mi => {
      const arrayIdx = toArrayIdx(mi);
      tots[mi] += parseFloat(costs[sec][key][arrayIdx]) || 0;
    });
  });

  return (
    <table className="dt" style={{ marginBottom: 4 }}>
      <thead>
        <tr>
          <th>{baseLabel} ({fc})</th>
          {monthCols.map(mi => <th key={mi}>{mlSafe(mi, startDate)}</th>)}
        </tr>
      </thead>
      <tbody>
        {COST_FIELDS.map(({ key, label: fl }) => {
          const isDevAdv = key === 'devAdvances' && sec === 'recoup';
          return (
            <tr key={key} className={isDevAdv ? 'rc' : 'ri'}>
              <td>{fl}{isDevAdv && <span style={{ fontSize: 8, color: 'var(--t4)', marginLeft: 6 }}>↑ from milestones</span>}</td>
              {monthCols.map(mi => {
                const arrayIdx = toArrayIdx(mi);
                const val = costs[sec][key][arrayIdx] ?? '';
                return (
                  <td key={mi}>
                    {isDevAdv
                      ? <span style={{ color: val ? 'var(--t1)' : 'var(--t4)', fontSize: 11 }}>{val ? fu(parseFloat(val)) : '—'}</span>
                      : <FormattedInput value={val} disabled={readOnly}
                          onChange={v2 => updateCS(game, sec, key, arrayIdx, v2)} />
                    }
                  </td>
                );
              })}
            </tr>
          );
        })}
        <tr className="rx"><td colSpan={monthCols.length + 1} /></tr>
        <tr className="rg">
          <td>Total {baseLabel} ({fc})</td>
          {monthCols.map(mi => <td key={mi}>{tots[mi] ? fu(tots[mi]) : '—'}</td>)}
        </tr>
      </tbody>
    </table>
  );
}

function CostsSection({ game, CS, updateCS, readOnly = false, startDate = null, devStartIdx = 0, functionalCurrency }) {
  const costs = getCostState(CS, game);
  const fc = functionalCurrency || 'USD';
  return (
    <div className="sb" style={{ marginTop: 14 }}>
      <div className="sb-head">
        <span className="sb-title">Costs ({fc})</span>
        {devStartIdx !== null && devStartIdx < 0 && startDate && (
          <span className="card-meta">
            From {(() => { const t = startDate.month + devStartIdx; const y = startDate.year + Math.floor(t/12); const m = ((t%12)+12)%12; return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m]+' '+y; })()}
          </span>
        )}
        {devStartIdx !== null && devStartIdx >= 0 && startDate && (
          <span className="card-meta">From {ml(devStartIdx, startDate)}</span>
        )}
        {devStartIdx !== null && devStartIdx >= 0 && !startDate && (
          <span className="card-meta">From Month {devStartIdx + 1}</span>
        )}
      </div>
      <div className="sb-body">
        <CostBlock costs={costs} sec="recoup"    game={game} updateCS={updateCS} readOnly={readOnly} startDate={startDate} devStartCol={devStartIdx ?? 0} functionalCurrency={functionalCurrency} />
        <div style={{ height: 16 }} />
        <CostBlock costs={costs} sec="nonRecoup" game={game} updateCS={updateCS} readOnly={readOnly} startDate={startDate} devStartCol={devStartIdx ?? 0} functionalCurrency={functionalCurrency} />
      </div>
    </div>
  );
}

function RoyaltiesSection({ game, PS, CS, DT, onGoToStanding, startDate, devStartIdx }) {
  const dt = getDealTerms(DT, game);
  const pL = dt.royaltyPreRecoup  ? fp(parseFloat(dt.royaltyPreRecoup))  : 'not set';
  const qL = dt.royaltyPostRecoup ? fp(parseFloat(dt.royaltyPostRecoup)) : 'not set';

  const enabledPlats = PLATS.filter(p => PS[game]?.[p.key]?.enabled);
  const aN = Array(NM).fill(0);
  enabledPlats.forEach(p => {
    calcPlatform(p.key, PS[game][p.key]).netRaw.forEach((v, i) => {
      if (v !== null) aN[i] += v;
    });
  });

  const costs = getCostState(CS, game);
  const aR = Array(NM).fill(0);
  COST_FIELDS.forEach(({ key }) => {
    costs.recoup[key].forEach((v, arrayIdx) => {
      const i = arrayIdx - PRE_MONTHS;
      if (i >= 0 && i < NM) aR[i] += parseFloat(v) || 0;
    });
  });

  const { netLessRecoup, cumulative, rateApplied, royaltyPayable } = calcRoyalties(aN, aR, dt);

  // Start from dev start date (clamped to 0) or first non-empty column
  const devCol = devStartIdx !== null && devStartIdx !== undefined ? Math.max(0, devStartIdx) : null;
  let firstCol = devCol ?? 0;
  if (devCol === null) {
    for (let i = 0; i < NM; i++) {
      if (aN[i] > 0 || aR[i] > 0) { firstCol = i; break; }
    }
  }
  const cols = Array.from({ length: NM - firstCol }, (_, i) => firstCol + i);

  return (
    <div className="sb" style={{ marginTop: 14 }}>
      <div className="sb-head">
        <span className="sb-title">Royalties Payable to Developer</span>
        <span className="card-meta">
          Pre {pL} · Post {qL}
          {(pL === 'not set' || qL === 'not set') && (
            <button onClick={onGoToStanding} style={{
              marginLeft: 10, background: 'none', border: 'none',
              color: 'var(--blue)', fontFamily: 'var(--sans)', fontSize: 8,
              letterSpacing: '.1em', textTransform: 'uppercase',
              cursor: 'pointer', textDecoration: 'underline'
            }}>
              Set in Standing Data →
            </button>
          )}
        </span>
      </div>
      <div className="sb-body">
        <table className="dt">
          <thead>
            <tr><th></th>{cols.map(i => <th key={i}>{ml(i, startDate)}</th>)}</tr>
          </thead>
          <tbody>
            <tr className="rc"><td>Net Revenue less Recoupable Costs</td>{cols.map(i=><td key={i}>{netLessRecoup[i]}</td>)}</tr>
            <tr className="rc"><td>Cumulative Position</td>{cols.map(i=><td key={i}>{cumulative[i]}</td>)}</tr>
            <tr className="rc"><td>Rate Applied</td>{cols.map(i=><td key={i}>{rateApplied[i]}</td>)}</tr>
            <tr className="rg"><td>Royalty Payable</td>{cols.map(i=><td key={i}>{royaltyPayable[i]}</td>)}</tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GameInputsPanel({ games, PS, CS, DT, updatePS, updateCS, onGoToStanding, readOnly = false, startDate = null, devStartDates = {}, onUpdateDevStart, functionalCurrency, convert, milestones = {}, onUpdateMilestones }) {
  const [curGame, setCurGame] = useState(games[0] || '');

  const devStartIdx = curGame !== '' ? (devStartDates[curGame] ?? null) : null;
  const setPlat = (platKey, enabled) => {
    updatePS(curGame, platKey, st => { st.enabled = enabled; });
  };

  return (
    <div className="panel">
      <div className="gi-bar">
        <span className="gi-lbl">Game</span>
        <select value={curGame} onChange={e => setCurGame(e.target.value)} style={{ minWidth: 190 }}>
          <option value="">— select game —</option>
          {games.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {!curGame && <p className="nomsg">Select a game above to begin modelling.</p>}
      {curGame && readOnly && <p className="read-only-badge" style={{ marginBottom: 12 }}>👁 View only</p>}

      {curGame && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span className="gi-lbl">Development Started</span>
          <select
            value={devStartIdx !== null ? devStartIdx : ''}
            disabled={readOnly}
            onChange={e => onUpdateDevStart?.(curGame, e.target.value !== '' ? parseInt(e.target.value) : null)}
          >
            <option value="">— select month —</option>
            {Array.from({ length: 60 }, (_, i) => {
              const idx = i - 24;
              let label;
              if (startDate) {
                const total = startDate.month + idx;
                const y = startDate.year + Math.floor(total / 12);
                const mIdx = ((total % 12) + 12) % 12;
                const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                label = MN[mIdx] + ' ' + y;
              } else {
                label = idx < 0 ? `${Math.abs(idx)} months before start` : `Month ${idx + 1}`;
              }
              return <option key={idx} value={idx}>{label}</option>;
            })}
          </select>

        </div>
      )}

      {curGame && (
        <>
          <div className="card">
            <div className="card-head"><span className="card-title">Platforms</span></div>
            <div className="card-body">
              <div className="tgl-grid">
                {PLATS.map(p => {
                  const on = PS[curGame]?.[p.key]?.enabled;
                  return (
                    <div key={p.key} className="tgl-item" style={{ justifyContent: 'flex-end', gap: 10 }}>
                      <span className="tgl-lbl" style={{ textAlign: 'right' }}>{p.label}</span>
                      <div className="tgl-pair">
                        <button className={`tbtn yes ${on ? 'on' : ''}`} onClick={() => !readOnly && setPlat(p.key, true)} disabled={readOnly}>On</button>
                        <button className={`tbtn no  ${!on ? 'on' : ''}`} onClick={() => !readOnly && setPlat(p.key, false)} disabled={readOnly}>Off</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {PLATS.filter(p => PS[curGame]?.[p.key]?.enabled).map(p => (
            <PlatformSection
              key={p.key}
              game={curGame} platKey={p.key} platLabel={p.label} badge={p.badge}
              PS={PS} updatePS={updatePS} readOnly={readOnly} startDate={startDate}
              functionalCurrency={functionalCurrency} convert={convert}
            />
          ))}

          <TotalsSection game={curGame} PS={PS} startDate={startDate} functionalCurrency={functionalCurrency} convert={convert} />
          <MilestonesSection game={curGame} milestones={milestones} onUpdateMilestones={onUpdateMilestones} readOnly={readOnly} startDate={startDate} functionalCurrency={functionalCurrency} DT={DT} />
          <CostsSection  game={curGame} CS={CS} updateCS={updateCS} readOnly={readOnly} startDate={startDate} devStartIdx={devStartIdx} functionalCurrency={functionalCurrency} />
          <RoyaltiesSection game={curGame} PS={PS} CS={CS} DT={DT} onGoToStanding={onGoToStanding} startDate={startDate} devStartIdx={devStartIdx} />
        </>
      )}
    </div>
  );
}
